import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import lang from '../../../utils/intl';
import { MfaMethod } from '../../types/mfaTypes';
import { Matomo, MatomoCategory } from 'common/Analytics/Matomo';
import { debounce } from 'lodash';
import axios, { AxiosResponse } from 'axios';
import { CircularProgress } from '@rmwc/circular-progress';
import { TrackJs } from 'common/analytics/TrackJs';
import Tippy from '@tippyjs/react';

export interface MfaFormProps {
    langId?: number;
    otpTtl: number;
    error?: string;
    otpTimer: number;
    sessionLeft: number;
    mfaMethod: MfaMethod;
    subjectHash?: string;
    lastPasswordChange?: number;
    rdtTtl: number;
    allowRdtCheckbox?: boolean;
}

interface VerifyResponse extends AxiosResponse {
    data: {
        error?: string;
        message?: string;
        rdt_subject_hash?: string;
        rdt_jwt_token?: string;
    };
}
interface ResendResponse extends AxiosResponse {
    data: {
        error?: string;
        otp_timer?: number;
        session_timer?: number;
    };
}

function MfaForm(props: MfaFormProps) {
    const { otpTtl = 10, error = '', otpTimer = 0, rdtTtl = 30, sessionLeft = 30, mfaMethod, subjectHash, allowRdtCheckbox = true } = props;
    const [otpInput, setOtpInput] = useState('');
    const [resendTimer, setResendTimer] = useState(otpTimer);
    const [sessionTimer, setSessionTimer] = useState(sessionLeft);
    const [isCheckingRdt, setIsCheckingRdt] = useState(false);
    const [errorState, setErrorState] = useState(error);
    const [isRdtChecked, setIsRdtChecked] = useState(false);

    useEffect(() => {
        handleRdt();
        loadOtpFromSms();
    }, []);

    useEffect(() => {
        if (sessionTimer > 0 && !isCheckingRdt) {
            const timer = setInterval(() => {
                setSessionTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
        if (sessionTimer < 1) {
            Matomo.push(MatomoCategory.MFA, 'MFA session expired');
            window.location.href = './?error=login.mfa_session.expired.error';
        }
    }, [sessionTimer]);

    useEffect(() => {
        if (resendTimer > 0 && !isCheckingRdt) {
            const timer = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [resendTimer]);

    useEffect(() => {
        Matomo.push(MatomoCategory.MFA, 'MFA failed logins');
    }, [error]);

    // Remove expired or invalid RDTs
    const cleanupRdtTokens = () => {
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('rdt.')) {
                try {
                    const token = localStorage.getItem(key);
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    // payload is in seconds, Date.now() in ms
                    if (payload.exp * 1000 < Date.now()) {
                        localStorage.removeItem(key);
                    }
                } catch (e) {
                    localStorage.removeItem(key);
                }
            }
        });
    };

    const handleRdt = async () => {
        const storedRdt = localStorage.getItem('rdt.' + subjectHash);
        if (storedRdt) {
            try {
                setIsCheckingRdt(true);
                const response: VerifyResponse = await axios.post('?page=mfa_api&cmd=verify_rdt', {
                    rdt: storedRdt,
                });
                if (checkRedirect(response)) {
                    return;
                }
                setIsCheckingRdt(false);
                localStorage.removeItem('rdt.' + subjectHash);
                cleanupRdtTokens();
            } catch (err) {
                setIsCheckingRdt(false);
                localStorage.removeItem('rdt.' + subjectHash);
                requestOtp();
                cleanupRdtTokens();
            }
        } else {
            requestOtp();
        }
    };

    const loadOtpFromSms = () => {
        // Invoke the WebOTP API
        if ('OTPCredential' in window) {
            const ac = new AbortController();
            navigator.credentials
                .get({
                    // @ts-ignore - otp does exist on navigator.credentials
                    otp: { transport: ['sms'] },
                    signal: ac.signal,
                })
                .then(otp => {
                    //@ts-ignore
                    setOtpInput(otp.code);
                })
                .catch(err => {
                    console.log(err);
                });
        }
    };

    useEffect(() => {
        if (error !== '') {
            Matomo.push(MatomoCategory.MFA, 'MFA failed logins');
        }
    }, [error]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement> = null) => {
        event?.preventDefault();
        Matomo.push(MatomoCategory['MFA'], 'All MFA logins');
        setErrorState('');
        try {
            const response: VerifyResponse = await axios.post('?page=mfa_api&cmd=verify_otp', {
                otp: otpInput,
                rdtCheck: isRdtChecked ? 'on' : '',
            });
            setRdtToken(response);
            setErrorState(response.data.message);
            checkRedirect(response);
        } catch (err) {
            setErrorState(err.response?.data?.message || err.message);
        }
    };

    const requestOtp = async () => {
        setErrorState('');
        try {
            if (resendTimer > 0) {
                return;
            }
            const response: ResendResponse = await axios.post('?page=mfa_api&cmd=resend');
            response.data?.otp_timer && setResendTimer(response.data.otp_timer);
            response.data?.session_timer && setSessionTimer(response.data.session_timer);
        } catch (err) {
            setErrorState(err.response?.data?.message ?? 'login.mfa.generic.error');
            checkRedirect(err.response);
        }
    };

    const checkRedirect = response => {
        switch (response?.status) {
            case 200:
                if (response?.data?.login_token) {
                    loginUser(response.data.login_token);
                    return true;
                }
                break;
            case 403:
                return (window.location.href = './?error=' + (response.data.error || ''));
            default:
                break;
        }
    };

    const loginUser = (loginToken: string) => {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = './';
        form.style.display = 'none';

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'login-token';
        input.value = loginToken;
        form.appendChild(input);

        document.body.appendChild(form);
        form.submit();
    };

    const setRdtToken = (response: VerifyResponse) => {
        try {
            if (response?.data?.rdt_subject_hash && response?.data?.rdt_jwt_token) {
                localStorage.setItem(`rdt.${response.data.rdt_subject_hash}`, response.data.rdt_jwt_token);
            }
        } catch (e) {
            console.error('Failed to store RDT token in localStorage');
            TrackJs.track(e);
        }
    };

    const handleLinkClick = debounce(() => {
        requestOtp();
    }, 300);

    return (
        <>
            {isCheckingRdt && (
                <div className="flex-column">
                    <div className="leaddesk-spinner">
                        <CircularProgress size="xlarge" />
                    </div>
                    <p>{lang('login.mfa.rdt_checking.label')}</p>
                </div>
            )}
            {!isCheckingRdt && (
                <div>
                    <form noValidate method="post" name="mfa-form" className="mfa-form" onSubmit={handleSubmit}>
                        <h1>{lang('generic.login_page.header.title')}</h1>
                        <p>
                            {lang(mfaMethod === MfaMethod.EMAIL ? 'login.mfa.email_instructions.info' : 'login.mfa.sms_instructions.info', {
                                'OTP-TTL': otpTtl.toString(),
                            })}
                        </p>
                        <div className="input-container">
                            <label htmlFor="otp">{lang('login.mfa_form.code.label')}</label>
                            <input
                                id="otp"
                                className={classNames(error ? 'input-error' : '')}
                                value={otpInput}
                                onChange={e => setOtpInput(e.target.value)}
                                name="otp"
                                autoComplete="one-time-code"
                                type="text"
                                pattern="\d{6}"
                                inputMode="numeric"
                                onInvalid={e => {
                                    // The pattern attribute above is needed for the autofill of the OTP
                                    // when received by sms and usage on phones,
                                    // but we don't want to show the default invalid popup
                                    e.preventDefault();
                                }}
                                required
                            />
                            {errorState && <div className="error">{lang(errorState)}</div>}
                        </div>
                        <div className="flex-row">
                            <Tippy
                                disabled={allowRdtCheckbox}
                                content={!allowRdtCheckbox ? lang('login.mfa.rdt_checkbox.tooltip') : ''}
                                arrow={true}
                                interactive={true}
                                placement="left"
                            >
                                <span className="flex-row">
                                    <input
                                        type="checkbox"
                                        id="rdtCheck"
                                        className="checkbox"
                                        name="rdtCheck"
                                        checked={isRdtChecked}
                                        disabled={!allowRdtCheckbox}
                                        onClick={() => setIsRdtChecked(!isRdtChecked)}
                                        // we have the onClick above modifying the state, but react requires a onChange handler as well
                                        onChange={() => {}}
                                    />
                                    <label
                                        htmlFor="rdtCheck"
                                        className={classNames(['pointer', 'remember-device-label'], { disabled: !allowRdtCheckbox })}
                                    >
                                        {lang('login.mfa.rdt_checkbox.label', { 'RDT-DAYS': rdtTtl })}
                                    </label>
                                </span>
                            </Tippy>
                        </div>
                        <button className="button" id="submit-button" type="submit">
                            {lang('generic.login.button')}
                        </button>
                    </form>
                    <div className="link-text resend-code-link-wrapper">
                        {resendTimer > 0 ? (
                            <a href={'#'} className={'resend-disabled'}>
                                {`${lang('login.mfa_form.resend.label')} ${resendTimer}s`}
                            </a>
                        ) : (
                            <a href={'#'} onClick={handleLinkClick} className={'link-text'}>
                                {lang('login.mfa_form.resend.label')}
                            </a>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

export default MfaForm;
